import { useCallback, useRef } from 'react';
import { api } from './api';
import { getDeviceId } from './deviceId';

interface Block {
  rangeStart: number;
  rangeEnd: number;
  next: number;
}

/**
 * Client-side allocator for m46-parking's pre-allocated notice-number blocks
 * (POST /officers/:id/notice-blocks — see
 * packages/modules/m46-parking/src/service/officers.ts's allocateNoticeBlock,
 * spec §3.3). That endpoint exists for offline mobile devices to pre-claim a
 * range of numbers so they can mint valid, globally-unique notice numbers
 * without a round-trip while offline; POST /contraventions itself does NOT
 * generate a noticeNumber for you — it takes a client-supplied one and only
 * enforces DB-level uniqueness, with no check that it falls in an allocated
 * block.
 *
 * This portal is a networked desktop app (no offline queue, per this
 * build's task brief), so it doesn't strictly need offline pre-allocation —
 * but using the real block-allocation endpoint and handing out sequential
 * numbers from it is the closest fit to the API's actual design, rather than
 * inventing an ad hoc numbering scheme with no server-side backing at all.
 * One block (the server's own default size, 500) comfortably covers a
 * single console session; a fresh block is requested transparently if it
 * ever runs out.
 */
export function useNoticeBlock(officerId: string | undefined) {
  const block = useRef<Block | null>(null);

  const takeNoticeNumber = useCallback(
    async (agency: string): Promise<string> => {
      if (!officerId) throw new Error('No officer record on this persona — cannot allocate a notice number');
      if (!block.current || block.current.next > block.current.rangeEnd) {
        const allocated = await api.post<{ rangeStart: number; rangeEnd: number }>(
          `/api/v1/parking/officers/${encodeURIComponent(officerId)}/notice-blocks`,
          { deviceId: getDeviceId() },
        );
        block.current = { rangeStart: allocated.rangeStart, rangeEnd: allocated.rangeEnd, next: allocated.rangeStart };
      }
      const n = block.current.next;
      block.current.next += 1;
      return `${agency}-${String(n).padStart(7, '0')}`;
    },
    [officerId],
  );

  return { takeNoticeNumber };
}
